import { DealItemState } from "src/app/features/prob/models/deal-item-state";
import { DealValue } from "src/app/features/prob/models/deal-value";
import { RateItem } from "src/app/features/prob/models/rate-item";
import { GridStateConfig } from "@mysrc/app/features/prob/models/grid-state-config";
import { FormMetaData } from "./form-meta-data";
import { Currency, DealItemDataChanges, DealValueGridData, EnumDataTypes, ResponseCurrency, ResponseDealCalculateSet } from ".";
import { EnumModelCodes } from "./constants/enumModelCodes";
import { ChangeDetectorRef } from "@angular/core";

/**
 * Manages the value state for deal items.
 * Used by components that manage multiple deal items.
 */
export class DealItemStates {

  // ----------------------- Constants ----------------------------------------

  /** Empty string guard value for ID checks */
  protected readonly ctEmptyString = '';

  // -------------------- Component State -------------------------------------

  /** Parent deal identifier */
  public dealId: string | null = null;

  // ------------------- Data Collections -------------------------------------

  /** Deal item states indexed by deal item ID */
  public itemStates = new Map<string, DealItemState>();

  /** Default rate grid table state, headers and data lookup optimizations */
  public rateTableState: GridStateConfig | null = null;

  // --------------- Item State Management ------------------------------------

  /**
   * Creates a new state entry for the given deal item ID.
   * Returns the existing entry if one already exists.
   */
  public add(dealItemId?: string | null, state: DealItemState | null = null, name: string | null = null): DealItemState | undefined {

    if (!dealItemId
      || dealItemId === this.ctEmptyString)
      return undefined;

    let item = this.itemStates.get(dealItemId);

    if (!item) {

      if (state) {
        this.itemStates.set(dealItemId, state);
        item = state;
      } else {
        item = new DealItemState();
        item.dealItemId = dealItemId;
        this.itemStates.set(dealItemId, item);
      }

      item.name = name;
    }

    return item;
  }

  /** Returns true if a state entry exists for the given deal item ID */
  public exists(dealItemId: string): boolean {

    if (!dealItemId
      || dealItemId === this.ctEmptyString)
      return false;

    return this.itemStates.has(dealItemId);
  }

  /** Returns the state data for the given deal item ID */
  public get(dealItemId?: string | null): DealItemState | undefined {

    if (!dealItemId
      || dealItemId === this.ctEmptyString)
      return undefined;

    return this.itemStates.get(dealItemId);
  }

  /** Removes the state data for the given deal item ID */
  public remove(dealItemId: string): void {

    this.itemStates.delete(dealItemId);
  }

  // ---------------- State Reset & Lifecycle ---------------------------------

  /** Returns true if any deal item state contains unsaved changes */
  public hasChanges(): boolean {

    let tmpHasChanges = false;

    this.itemStates.forEach((itemState: DealItemState) => {
      if (itemState.hasChanges())
        tmpHasChanges = true;
    });

    return tmpHasChanges;
  }

  /** Resets change tracking across all item states while preserving data */
  public reset(): void {

    this.itemStates.forEach(itemState => {
      itemState.reset();
    });
  }

  /** Clears all state data and resets the rate table state */
  public clearAll(): void {

    this.itemStates.clear();
    this.rateTableState = null;
  }

  /** Marks all rate items as live and removes edit flags */
  public markAsLive(): void {

    this.itemStates.forEach(itemState => {

      itemState.rateItems?.forEach(item => {
        item.isEditItem = false;
      });

      // TODO: Temporary fix to remove createdFor from deal values.
      // Needs testing as live deal values for the user may be removed.
      itemState.dealValues?.forEach(item => {
        item.createdFor = null;
      });
    });
  }

  // ------------------- Form Meta Data ---------------------------------------

  /** Returns the form meta data for the given deal item ID, creating it if absent */
  public getFormMeta(dealItemId?: string | null): FormMetaData | undefined | null {

    if (!dealItemId
      || dealItemId === this.ctEmptyString)
      return undefined;

    let state = this.add(dealItemId, null);
    if (!state)
      return undefined;

    if (state.formMetaData == null)
      state.formMetaData = new FormMetaData();

    return state.formMetaData;
  }

  /** Resets form meta data handlers for all deal item states */
  public resetForm(): void {

    this.itemStates.forEach(itemState => {
      itemState.formMetaData?.resetHandlers();
    });
  }

  // ------------------- Deal Value Operations --------------------------------

  /** Returns deal values for the given deal item ID */
  public getDataValues(dealItemId?: string | null): DealValue[] {

    if (!dealItemId
      || dealItemId === this.ctEmptyString)
      return [];

    let data = Array.from(this.itemStates.get(dealItemId)?.dealValues.values() ?? []);
    if (!data)
      return [];

    return data;
  }

  /**
   * Stores or updates deal values for the given deal item ID.
   * Accepts partial value lists when sourced from the UI.
   */
  public setDataValues(
    isLoading: boolean,
    dealItemId?: string | null,
    values?: DealValue[] | null,
    gridValues?: DealValueGridData[] | null,
    injectedValues?: DealValue[] | null
  ): void {

    if (!dealItemId
      || dealItemId === this.ctEmptyString)
      return;

    let state: DealItemState | undefined = this.add(dealItemId, null);
    if (!state)
      return;

    state.setInjectedValues(injectedValues);

    state.syncValuesForChanges(isLoading, values);

    state.syncGridValuesForChanges(isLoading, gridValues);
  }

  // ------------------- Rate Item Operations ---------------------------------

  /** Returns rate items for the given deal item ID, filtering out empty entries */
  public getDataRates(dealItemId?: string | null): RateItem[] | undefined {

    if (!dealItemId
      || dealItemId === this.ctEmptyString)
      return undefined;

    let data = this.itemStates.get(dealItemId)?.rateItems;
    if (!data)
      return [];

    for (let i = data.length; i >= 0; i--) {
      let item = data[i];

      if (!item
        || item.idxVal1 === null
        || item.idxVal1 === undefined
        || item.idxVal1.length < 1) {

        data.splice(i, 1);
      }
    }

    return data;
  }

  // --------------- Data Change Operations -----------------------------------

  /**
   * Collects deal item data changes for all items, or for a specific deal item ID.
   * Returns only input deal values and edited rate items.
   */
  public getDataChanges(dealItemId?: string | null): DealItemDataChanges[] | null {

    let changes: DealItemDataChanges[] = [];

    if (!dealItemId
      || dealItemId === this.ctEmptyString) {

      const entries = Array.from(this.itemStates.entries());

      for (let i = 0; i < entries.length; i++) {

        const [, itemState] = entries[i];
        const itemChanges = itemState.getDataChanges();
        if (itemChanges) {
          changes.push(itemChanges);
        }
      }

    } else {

      let itemState = this.itemStates.get(dealItemId);

      if (itemState) {
        let itemChanges = itemState.getDataChanges();
        if (itemChanges)
          changes.push(itemChanges);
      }
    }

    if (changes.length < 1)
      return null;

    return changes;
  }

  /**
   * Processes server response for deal item data changes.
   * Resets state when excludeChanges is flagged and restores values from server data.
   */
  public setDataChanges(resp: ResponseDealCalculateSet | null, respCurr: ResponseCurrency | null): void {

    if (resp?.excludeChanges === true) {
      this.reset();
    }

    this.applyCurrencyResponse(respCurr);

    this.applyCalculateResponse(resp);
  }

  // -------------- Calculation Date Operations -------------------------------

  /**
   * Updates LastCalculatedOn date values across all deal item states.
   * Sets dtVal to current UTC date when setToNow is true, otherwise nulls it.
   */
  public updateCalcOnDates(setToNow: boolean): void {

    const newDate = setToNow ? new Date() : null;

    this.itemStates.forEach((itemState: DealItemState) => {

      if (!itemState.dealValues)
        return;

      itemState.dealValues.forEach((dealValue: DealValue) => {

        if (dealValue.code === EnumModelCodes.LastCalculatedOn)
          dealValue.dtVal = newDate;
      });
    });
  }

  /** Returns the LastCalculatedOn date filtered by live or draft status */
  public getCalcOnDate(isLive: boolean): Date | null {

    for (const itemState of this.itemStates.values()) {

      if (!itemState.dealValues)
        continue;

      for (const dealValue of itemState.dealValues.values()) {

        if (dealValue.code === EnumModelCodes.LastCalculatedOn
          && ((isLive === true && dealValue.createdFor === null)
            || (isLive === false && dealValue.createdFor))) {

          return dealValue.dtVal ?? null;
        }
      }
    }

    return null;
  }

  // ------------- Currency Injected Values -----------------------------------

  /** Dynamically creates currency deal values from the currency lookup response */
  public createInjectedCurrencyValues(curr: Currency, dealItemId?: string | null): DealValue[] | null {

    const result: DealValue[] = [];
    const dealId = this.dealId;

    let dvSymbol: DealValue = new DealValue();
    dvSymbol.dealId = dealId;
    dvSymbol.dealItemId = dealItemId;
    dvSymbol.sVal = curr.symbol;
    dvSymbol.dataTypeCode = EnumDataTypes.String;
    dvSymbol.code = EnumModelCodes.CurrencySymbol;

    result.push(dvSymbol);

    let dvCode: DealValue = new DealValue();
    dvCode.dealId = dealId;
    dvCode.dealItemId = dealItemId;
    dvCode.sVal = curr.code;
    dvCode.dataTypeCode = EnumDataTypes.String;
    dvCode.code = EnumModelCodes.CurrencyCode;

    result.push(dvCode);

    return result;
  }

  // -------------------- API Calls -------------------------------------------

  /** Applies currency response data to the relevant item states */
  protected applyCurrencyResponse(respCurr: ResponseCurrency | null): void {

    if (!respCurr?.data)
      return;

    for (const curr of respCurr.data) {

      const itemState = this.itemStates.get(curr.key!);

      if (itemState) {
        const currencyValues = this.createInjectedCurrencyValues(curr, curr.key);
        itemState.updateInjectedCurrencyValues(currencyValues);
      }
    }
  }

  /** Applies calculate response data, resetting and restoring each item state */
  protected applyCalculateResponse(resp: ResponseDealCalculateSet | null): void {

    if (!resp?.data)
      return;

    for (let i = 0; i < resp.data.length; i++) {

      const item = resp.data[i];
      const itemState = this.itemStates.get(item.dealItemId!);

      if (itemState) {
        itemState.reset();
        itemState.restoreFromServer(item.dealValues);
      }
    }
  }
}
